import React, { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  lionAutoTemplateKeys, useLionAutoTemplateShows, useApplyLionAutoTemplate,
} from '../../hooks/useLionAutoTemplates';
import { formatEventDateTime } from '../../utils/dates';
import {
  showKey, showStatus, defaultShowSelection, tickedShows, everyTicked, otherGroupsText, applyConfirmText,
  runTemplateApply, untickDone, rowStateText, applyRunText, templateErrorText, templateReadRetryable,
  applyVersionOf, showsVersionChanged, templateStaleText, applyBlockedReason, showsCacheNote,
} from '../../utils/lionAutoTemplates';
import { toggleSelection } from '../../utils/lionAutoChecker';

// Templates → "Apply to shows…": the artist's upcoming shows (the ones we
// hold listings at, and any already linked), in date order, each with how
// it stands against this version and the groups of its own. Same-venue
// shows without this version start ticked. Apply sends the version the
// pricer saw in the list (template.version) and runs ONE show at a time
// (runTemplateApply awaits each before the next), each row showing its
// state: waiting, applying, its result (warn style when partial: groups
// skipped, sections not found or listings that stayed), an unknown outcome
// (warn: reload to check) or its error. An error marks its row and the run
// goes on; a 409 TEMPLATE_STALE (re-saved since the list loaded), a 429 or
// a 404 (the template deleted) stops it. When the shows answer carries
// another version than the list showed, the stale banner shows and Apply
// waits for Reload. "Stop after this show" lets the show in flight finish
// and sends nothing more. Only the cleanly applied shows untick afterwards.
// Ticks are held as null until touched (the defaults follow the list), then
// frozen for the run, since the list re-reads after each show. Show names
// and venues from Skybox render as text. Renders the modal's body and footer.
//
// Props: template (the TemplateView, or null when the list no longer has
// it); onBack(); onRunningChange(running), called when a run starts and
// ends (the modal blocks closing meanwhile).

const STATUS_TITLES = {
  source: 'The show this template was saved from',
  current: "This show's linked groups carry this version",
  older: "This show's linked groups carry an older version: apply again to bring them up to date",
  none: 'No group on this show is linked to this template yet',
  applied: 'This show has groups linked to this template',
};

const HINT = 'Same-venue dates without this version start ticked. Shows are applied one at a time. '
  + 'On each show the groups it is missing are created (Auto off) and ranked; its linked groups take the '
  + "template's sections, rows, Qty, splits and notes filter, and keep their floor, ladder and hold; listings on "
  + "the show's own groups that a template group fits move into it, ranked ones too. A date added later needs another Apply.";
const RUNNING_TITLE = 'Wait for the run to finish, or stop it after this show';

const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

function statusTitle(status, show) {
  const linked = Number(show.linked) || 0;
  const auto = Number(show.linkedAuto) || 0;
  const base = STATUS_TITLES[status.key] || '';
  if (!linked) return base;
  return `${base} · ${plural(linked, 'linked group')}${auto ? `, ${auto} on Auto` : ''}`;
}

function ShowRow({ show, version, checked, locked, state, onToggle }) {
  const status = showStatus(show, version);
  const others = otherGroupsText(show);
  const run = rowStateText(state);
  const date = formatEventDateTime(show.eventDate);
  const name = show.eventName || `#${show.eventId}`;
  return (
    <tr className={checked ? undefined : 'off'}>
      <td>
        <input
          type="checkbox"
          checked={checked}
          disabled={locked}
          title={locked ? RUNNING_TITLE : undefined}
          onChange={() => onToggle(showKey(show))}
          aria-label={`Apply to ${name} ${date}`}
        />
      </td>
      <td className="la-template-date">{date || '?'}</td>
      <td className="la-import-name" title={name}>{name}</td>
      <td className="la-import-name" title={show.venue || undefined}>
        {show.venue || '—'}
        {show.sameVenue !== true && <span className="la-hint"> · other venue</span>}
      </td>
      <td><span className={`la-run-chip ${status.kind}`} title={statusTitle(status, show)}>{status.text}</span></td>
      <td>{others}</td>
      <td className={`la-template-result${run ? ` ${run.kind}` : ''}`}>{run?.text}</td>
    </tr>
  );
}

function LaTemplateApplyList({ template, onBack, onRunningChange }) {
  const queryClient = useQueryClient();
  // No template (deleted elsewhere): a disabled query on its own key, never the shows prefix
  const { data, isLoading, isError, error, isFetching, refetch } = useLionAutoTemplateShows(template?.id ?? null, !!template);
  const applyM = useApplyLionAutoTemplate();
  // null = untouched: the default ticks (defaultShowSelection)
  const [selectedState, setSelectedState] = useState(null);
  const [runs, setRuns] = useState({}); // show key → its row state in the last run
  const [running, setRunning] = useState(false);
  const [stopAsked, setStopAsked] = useState(false);
  const [stop, setStop] = useState(null); // why the last run stopped: { kind, message } | null
  const stopRef = useRef(false);
  const aliveRef = useRef(true); // false once unmounted: a run sends nothing more

  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  const shows = Array.isArray(data?.shows) ? data.shows : [];
  // The statuses read against the shows answer's version; Apply sends the list's
  const version = data?.version ?? template?.version ?? null;
  const selected = selectedState ?? defaultShowSelection(shows, version);
  const keys = shows.map(showKey);
  const ticked = tickedShows(selected, shows);
  const allTicked = everyTicked(selected, keys);
  const versionChanged = showsVersionChanged(template, data);
  const blocked = applyBlockedReason({
    template, showsData: data, isFetching, running, stale: stop?.kind === 'stale', tickedCount: ticked.length,
  });
  const groupCount = Array.isArray(template?.groups) ? template.groups.length : 0;
  const runText = applyRunText(runs);
  const cacheNote = showsCacheNote(data);

  const handleToggle = (key) => setSelectedState(prev => toggleSelection(prev ?? selected, key));
  const handleToggleAll = () => setSelectedState(allTicked ? new Set() : new Set(keys));
  const handleStop = () => {
    stopRef.current = true;
    setStopAsked(true);
  };

  const handleApply = async () => {
    const runVersion = applyVersionOf(template);
    if (blocked || runVersion === null) return;
    const confirmText = applyConfirmText(ticked);
    if (confirmText && !window.confirm(confirmText)) return;
    const id = template.id;
    const batch = ticked;
    // The ticks hold still while the list re-reads after each show
    setSelectedState(new Set(selected));
    stopRef.current = false;
    setStopAsked(false);
    setStop(null);
    setRuns({});
    setRunning(true);
    onRunningChange?.(true);
    try {
      const out = await runTemplateApply(
        batch,
        (show) => applyM.mutateAsync({ id, eventId: show.eventId, version: runVersion }),
        {
          onUpdate: (key, state) => setRuns(prev => ({ ...prev, [key]: state })),
          shouldContinue: () => aliveRef.current && !stopRef.current,
        }
      );
      setStop(out.stop);
      setSelectedState(prev => untickDone(prev, out.results));
    } finally {
      setRunning(false);
      onRunningChange?.(false);
    }
  };

  // Re-saved since the list loaded, or gone: the templates and this list
  // re-read (the new version and its statuses), and the ticks start from the
  // defaults again
  const handleReload = () => {
    setRuns({});
    setStop(null);
    setSelectedState(null);
    applyM.reset();
    queryClient.invalidateQueries({ queryKey: lionAutoTemplateKeys.all });
  };

  const retryButton = templateReadRetryable(error) && (
    <button
      type="button"
      className="btn btn-secondary btn-sm la-status-action"
      onClick={() => refetch()}
      disabled={isFetching}
      title={isFetching ? 'Reading the shows…' : undefined}
    >
      {isFetching ? 'Retrying…' : 'Retry'}
    </button>
  );
  const reloadButton = (
    <button
      type="button"
      className="btn btn-secondary btn-sm la-status-action"
      onClick={handleReload}
      disabled={running}
      title={running ? RUNNING_TITLE : 'Read the templates and the shows again'}
    >
      Reload
    </button>
  );

  // The banner of a stopped run (stale, rate limited, gone) or of a shows
  // answer read at another version; "Stop after this show" says so in the footer
  let stopBanner = null;
  if (stop && stop.kind !== 'stopped') {
    stopBanner = (
      <div className={`la-status ${stop.kind === 'rate_limited' ? 'warn' : 'error'}`}>
        {stop.message}
        {stop.kind !== 'rate_limited' && reloadButton}
      </div>
    );
  } else if (versionChanged) {
    stopBanner = <div className="la-status error">{templateStaleText(data?.version)}{reloadButton}</div>;
  }

  const headLine = template && (
    <div className="la-import-show">
      <strong title={template.name}>{template.name}</strong>
      <span className="la-muted">
        {[template.version ? `version ${template.version}` : null, plural(groupCount, 'group'), template.venue || null]
          .filter(Boolean).join(' · ')}
      </span>
      {isFetching && !isLoading && <span className="la-hint">Refreshing…</span>}
    </div>
  );

  let body;
  if (!template) {
    body = <div className="la-empty">This template is gone. Go back to the list.</div>;
  } else if (isLoading) {
    body = <div className="la-empty">Reading the artist&apos;s upcoming shows…</div>;
  } else if (isError && !data) {
    body = (
      <div className={`la-status${templateReadRetryable(error) ? ' error' : ''}`}>
        {templateErrorText(error, { read: true })}{retryButton}
      </div>
    );
  } else if (!shows.length) {
    body = (
      <>
        {headLine}
        {cacheNote && <div className="la-status warn">{cacheNote}</div>}
        <div className="la-empty">
          No upcoming shows of this artist that you can price. Only shows we hold listings at, and shows already
          linked to this template, are listed.
        </div>
      </>
    );
  } else {
    body = (
      <>
        {headLine}
        {isError && (
          <div className="la-status error">
            Could not refresh the shows (showing the last list): {templateErrorText(error, { read: true })}
            {retryButton}
          </div>
        )}
        {stopBanner}
        {cacheNote && <div className="la-status warn">{cacheNote}</div>}
        <table className="la-import-table la-template-table">
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={allTicked}
                  disabled={running || !keys.length}
                  title={running ? RUNNING_TITLE : undefined}
                  onChange={handleToggleAll}
                  aria-label="Tick every show"
                />
              </th>
              <th>Date</th>
              <th>Show</th>
              <th>Venue</th>
              <th>Status</th>
              <th>Its own groups</th>
              <th>Result</th>
            </tr>
          </thead>
          <tbody>
            {shows.map(s => {
              const key = showKey(s);
              return (
                <ShowRow
                  key={key}
                  show={s}
                  version={version}
                  checked={selected.has(key)}
                  locked={running}
                  state={runs[key]}
                  onToggle={handleToggle}
                />
              );
            })}
          </tbody>
        </table>
        <div className="la-import-warnings"><span className="la-hint">{HINT}</span></div>
      </>
    );
  }

  const applyLabel = running ? 'Applying…' : `Apply to ${plural(ticked.length, 'show')}`;

  return (
    <>
      <div className="modal-body la-template-body">{body}</div>
      <div className="modal-footer bulk-review-footer">
        <span className="ai-admin-note" aria-live="polite">{runText || `${ticked.length} selected`}</span>
        {!running && stop?.kind === 'stopped' && <span className="ai-admin-note">{stop.message}</span>}
        <span style={{ flex: 1 }} />
        <button
          type="button"
          className="btn-secondary"
          onClick={onBack}
          disabled={running}
          title={running ? RUNNING_TITLE : undefined}
        >
          Back
        </button>
        {running && (
          <button
            type="button"
            className="btn-secondary"
            onClick={handleStop}
            disabled={stopAsked}
            title={stopAsked ? 'The show being applied finishes; nothing after it is sent' : undefined}
          >
            {stopAsked ? 'Stopping after this show…' : 'Stop after this show'}
          </button>
        )}
        {template && data && (
          <button
            type="button"
            className="btn-primary"
            onClick={handleApply}
            disabled={!!blocked}
            title={blocked || undefined}
          >
            {applyLabel}
          </button>
        )}
      </div>
    </>
  );
}

export default LaTemplateApplyList;
